import { GraduationCap, CarFront } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { RoadBanner } from "@/components/illustrations/RoadBanner";
import { Seo } from "@/components/Seo";

const About = () => {
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36">
      <Seo
        title="About DELSHA — Driving Lesson Booking App"
        description="DELSHA is a driving lesson booking app connecting learner drivers with driving instructors. Learn how DELSHA helps learners and instructors."
      />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
            About DELSHA
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Learning to drive, made simpler.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            DELSHA is a driving lesson booking app that connects learner drivers
            with driving instructors. It brings discovering instructors, booking
            lessons and managing bookings into one clear, easy-to-use place.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-14">
          <RoadBanner />
        </Reveal>

        <div className="grid gap-6 py-16 md:grid-cols-2 md:py-24">
          <Reveal>
            <div
              data-testid="about-learners-card"
              className="h-full rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
                <GraduationCap className="h-6 w-6" />
              </span>
              <h2 className="mt-6 font-display text-xl font-semibold text-slate-900 sm:text-2xl">
                For learners
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">
                Finding the right instructor no longer means endless phone calls
                and waiting for replies. With DELSHA you can discover driving
                instructors near you, compare your options, book lessons at
                times that fit your life and keep every booking organised — from
                your very first lesson to test day.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div
              data-testid="about-instructors-card"
              className="h-full rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
                <CarFront className="h-6 w-6" />
              </span>
              <h2 className="mt-6 font-display text-xl font-semibold text-slate-900 sm:text-2xl">
                For instructors
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">
                DELSHA gives driving instructors a straightforward way to be
                discovered by local learners, receive bookings and manage their
                lesson schedule in one place — with online payments supported
                through Stripe where available, and the freedom to offer cash
                payments to learners directly.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-slate-200/80 bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              What guides us
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {["Clarity", "Trust", "Flexibility", "Learner first"].map((v, i) => (
              <Reveal key={v} delay={i * 0.08}>
                <div
                  data-testid={`about-value-${v.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-slate-200/80 bg-white px-6 py-5 text-center shadow-sm"
                >
                  <span className="font-display text-base font-semibold text-slate-900">
                    {v}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
